import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Receipt, Repeat, Search, CalendarDays, Download, RefreshCw, Sparkles, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import SearchableSelect from "../../components/ui/SearchableSelect";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { DateRangeFilter } from "../../components/common/DateRangeFilter";
import ExpenseFormDialog from "../../components/expenses/ExpenseFormDialog";
import { API } from "../../App";
import { useTimezone } from "../../contexts/TimezoneContext";
import { DEFAULT_DATE_FILTER, toYMD, toYMDInTimezone } from "../../utils/dateUtils";

/**
 * Expenses page — full CRUD view for operating expenses.
 *
 * Rolls up totals into the Financials → General tab's Net Profit formula via
 * /api/expenses/summary. This page writes; the Financials tab reads.
 */
const ExpensesPage = () => {
  const { timezone, formatDate } = useTimezone();
  // Deep-links from the dashboard: ?category=X&date_filter=Y[&start_date&end_date]
  const [searchParams] = useSearchParams();

  const [dateFilter, setDateFilter] = useState(() => searchParams.get("date_filter") || DEFAULT_DATE_FILTER);
  // Defaults for the custom date range are "today" *in the business timezone*
  // (not the browser's) so users in different time zones see the same date
  // pre-filled as the business does. `toYMDInTimezone` falls back to
  // browser-local if the tz isn't loaded yet — TimezoneContext re-fires the
  // useEffect once it resolves the business timezone.
  const [customStart, setCustomStart] = useState(() => searchParams.get("start_date") || toYMDInTimezone(new Date(), null));
  const [customEnd, setCustomEnd] = useState(() => searchParams.get("end_date") || toYMDInTimezone(new Date(), null));
  const [category, setCategory] = useState(() => searchParams.get("category") || "all");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Delete confirm state
  const [deleting, setDeleting] = useState(null);
  // Cancel-recurring confirm state
  const [cancellingRecurring, setCancellingRecurring] = useState(null);
  // Run-recurring-now state
  const [runningRecurring, setRunningRecurring] = useState(false);

  // Sync the custom-range defaults to today *in the business timezone* once
  // it's loaded from /api/settings/business. Only updates while the values
  // are still at their initial browser-local "today" — never clobbers a
  // date the user already picked.
  const customRangeTouched = useRef(!!searchParams.get("start_date"));
  useEffect(() => {
    if (customRangeTouched.current) return;
    if (!timezone) return;
    const businessToday = toYMDInTimezone(new Date(), timezone);
    setCustomStart(businessToday);
    setCustomEnd(businessToday);
  }, [timezone]);

  // Fetch categories once
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get(`${API}/expenses/categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCategories(res.data.categories || []))
      .catch(() => {});
  }, []);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ date_filter: dateFilter });
      if (dateFilter === "custom") {
        if (customStart) params.append("start_date", customStart);
        if (customEnd) params.append("end_date", customEnd);
      }
      if (category !== "all") params.append("category", category);
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
      if (timezone) params.append("tz", timezone);
      const res = await axios.get(`${API}/expenses?${params.toString()}`, { headers });
      setItems(res.data.items || []);
      setTotal(res.data.total_amount || 0);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customStart, customEnd, category, debouncedSearch, timezone]);

  const formatCurrency = (v) =>
    `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleAdd = () => { setEditing(null); setDialogOpen(true); };
  const handleEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const handleRunRecurring = async () => {
    setRunningRecurring(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/expenses/run-recurring`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { generated, skipped_existing } = res.data || {};
      if (generated > 0) {
        toast.success(`Generated ${generated} recurring expense${generated === 1 ? "" : "s"} for this month`);
        fetchList();
      } else {
        toast.info(`All caught up — no new copies needed (${skipped_existing} already present)`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to run recurring pass");
    } finally {
      setRunningRecurring(false);
    }
  };

  const handleCancelRecurring = async () => {
    if (!cancellingRecurring) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/expenses/${cancellingRecurring.expense_id}/cancel-recurring`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tplId = res.data?.template_id;
      toast.success("Recurring cancelled — no more monthly copies will be created");
      setItems((prev) => prev.map((x) => {
        if (x.expense_id === tplId) return { ...x, is_recurring: false };
        if (x.recurring_source_id === tplId) return { ...x, recurring_source_id: null };
        return x;
      }));
      setCancellingRecurring(null);
    } catch {
      toast.error("Failed to cancel recurring");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/expenses/${deleting.expense_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Expense deleted");
      setItems((prev) => prev.filter((x) => x.expense_id !== deleting.expense_id));
      setTotal((t) => Math.max(0, t - (deleting.amount || 0)));
      setDeleting(null);
    } catch {
      toast.error("Failed to delete expense");
    }
  };

  // CSV export
  const exportCSV = () => {
    if (!items.length) { toast.error("No expenses to export"); return; }
    const header = ["Date", "Description", "Category", "Amount", "Recurring", "Notes"];
    const rows = items.map((e) => [
      e.date, `"${(e.description || "").replace(/"/g, '""')}"`,
      e.category, e.amount,
      e.is_recurring ? "Yes" : "No",
      `"${(e.notes || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expenses_${dateFilter}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Expenses exported");
  };

  const categoryBadgeClass = (cat) => {
    // Subtle accent per category — muted, not busy
    const map = {
      Rent: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      Utilities: "bg-cyan-500/10 text-cyan-500 border-cyan-500/30",
      Supplies: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
      Marketing: "bg-pink-500/10 text-pink-500 border-pink-500/30",
      Software: "bg-violet-500/10 text-violet-500 border-violet-500/30",
      Insurance: "bg-indigo-500/10 text-indigo-500 border-indigo-500/30",
      Vehicle: "bg-orange-500/10 text-orange-500 border-orange-500/30",
      Travel: "bg-amber-500/10 text-amber-500 border-amber-500/30",
      Meals: "bg-red-500/10 text-red-500 border-red-500/30",
      Office: "bg-teal-500/10 text-teal-500 border-teal-500/30",
      "Professional Fees": "bg-purple-500/10 text-purple-500 border-purple-500/30",
      Taxes: "bg-rose-500/10 text-rose-500 border-rose-500/30",
      Other: "bg-gray-500/10 text-gray-400 border-gray-500/30",
    };
    return map[cat] || map.Other;
  };

  const savedHandler = (saved) => {
    // After save, refresh from server to ensure date-window filter stays accurate
    fetchList();
  };

  const recurringCount = useMemo(() => items.filter((e) => e.is_recurring).length, [items]);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/30">
              <Receipt className="w-5 h-5 text-[#FFCC00]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="expenses-heading">Expenses</h2>
              <p className="text-muted-foreground text-sm">Track operating costs that roll into Net Profit</p>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <CalendarDays className="w-4 h-4 text-muted-foreground mt-2" />
          <DateRangeFilter
            value={dateFilter}
            onChange={setDateFilter}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={({ start, end }) => { customRangeTouched.current = true; setCustomStart(start); setCustomEnd(end); }}
            triggerClassName="w-[160px] bg-theme-surface border-theme text-foreground"
            dataTestId="expenses-date-filter"
          />
        </div>
      </div>

      {/* Filter bar */}
      <Card className="glass-card border-theme">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search description…"
                className="pl-9 bg-theme-surface border-theme"
                data-testid="expenses-search"
              />
            </div>
            <div className="w-[200px]">
              <SearchableSelect
                value={category}
                onChange={setCategory}
                options={[
                  { value: "all", label: "All categories" },
                  ...categories.map((c) => ({ value: c, label: c })),
                ]}
                placeholder="All categories"
                searchThreshold={4}
                triggerClassName="bg-theme-surface border-theme h-10"
                data-testid="expenses-category-filter"
              />
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:ml-auto">
              <Button
                variant="outline"
                onClick={handleRunRecurring}
                disabled={runningRecurring}
                data-testid="expenses-run-recurring-btn"
                title="Generate this month's recurring-expense copies now"
                className="flex-1 sm:flex-initial"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${runningRecurring ? "animate-spin" : ""}`} />
                <span className="truncate">{runningRecurring ? "Running…" : "Run recurring"}</span>
              </Button>
              <Button variant="outline" onClick={exportCSV} data-testid="expenses-export-csv" className="flex-1 sm:flex-initial">
                <Download className="w-4 h-4 mr-1.5" /> CSV
              </Button>
              <Button onClick={handleAdd} className="bg-[#0046BE] hover:bg-[#0046BE]/90 text-white flex-1 sm:flex-initial" data-testid="expense-add-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Expense
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="glass-card border-theme">
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total expenses</p>
            <p className="text-2xl font-bold text-[#FFCC00] mt-1 font-['Manrope']" data-testid="expenses-total">{formatCurrency(total)}</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Entries</p>
            <p className="text-2xl font-bold text-foreground mt-1 font-['Manrope']" data-testid="expenses-count">{items.length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Repeat className="w-3 h-3" /> Recurring monthly
            </p>
            <p className="text-2xl font-bold text-foreground mt-1 font-['Manrope']" data-testid="expenses-recurring-count">{recurringCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base font-['Manrope']">Expense Ledger</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-muted-foreground py-8">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted-foreground py-10" data-testid="expenses-empty">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No expenses recorded for this period</p>
              <Button variant="outline" size="sm" onClick={handleAdd} className="mt-3">
                <Plus className="w-4 h-4 mr-1.5" /> Add your first expense
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-theme hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">Description</TableHead>
                    <TableHead className="text-muted-foreground">Category</TableHead>
                    <TableHead className="text-muted-foreground text-right">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Recurring</TableHead>
                    <TableHead className="text-muted-foreground text-right w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((e) => (
                    <TableRow key={e.expense_id} className="border-theme hover:bg-theme-surface" data-testid={`expense-row-${e.expense_id}`}>
                      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                        {formatDate(e.date, { month: "short", day: "numeric", year: "numeric" })}
                      </TableCell>
                      <TableCell className="text-foreground font-medium">
                        <div className="flex items-center gap-2">
                          <span>{e.description}</span>
                          {e.recurring_source_id && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/30"
                              title="Auto-generated from a recurring expense"
                              data-testid={`expense-auto-pill-${e.expense_id}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" /> Auto
                            </span>
                          )}
                        </div>
                        {e.notes && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{e.notes}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${categoryBadgeClass(e.category)}`}>{e.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-foreground">{formatCurrency(e.amount)}</TableCell>
                      <TableCell>
                        {e.is_recurring || e.recurring_source_id ? (
                          <div className="inline-flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 text-xs ${e.is_recurring ? "text-[#FFCC00]" : "text-muted-foreground"}`}>
                              <Repeat className="w-3 h-3" /> {e.is_recurring ? "Monthly" : "Auto"}
                            </span>
                            <button
                              type="button"
                              onClick={() => setCancellingRecurring(e)}
                              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                              title="Cancel recurring — stop future monthly copies"
                              data-testid={`expense-cancel-recurring-${e.expense_id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(e)} data-testid={`expense-edit-${e.expense_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleting(e)} data-testid={`expense-delete-${e.expense_id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total row */}
                  <TableRow className="border-theme bg-theme-surface">
                    <TableCell colSpan={3} className="text-right font-semibold text-foreground">Total</TableCell>
                    <TableCell className="text-right font-bold text-[#FFCC00]" data-testid="expenses-table-total">{formatCurrency(total)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ExpenseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        categories={categories}
        onSaved={savedHandler}
      />

      <AlertDialog open={Boolean(cancellingRecurring)} onOpenChange={(v) => !v && setCancellingRecurring(null)}>
        <AlertDialogContent data-testid="expense-cancel-recurring-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this recurring expense?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancellingRecurring
                ? `"${cancellingRecurring.description}" — ${formatCurrency(cancellingRecurring.amount)}/month. ${
                    cancellingRecurring.recurring_source_id && !cancellingRecurring.is_recurring
                      ? "This entry stays; the monthly recurrence that auto-creates it will be stopped."
                      : "Existing entries stay; no future monthly copies will be created."
                  }`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expense-cancel-recurring-keep-btn">Keep recurring</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelRecurring}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="expense-cancel-recurring-confirm-btn"
            >
              Cancel recurring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="expense-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting ? `"${deleting.description}" — ${formatCurrency(deleting.amount)}. This can't be undone.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="expense-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesPage;
